import React, { useEffect, useRef, useState } from 'react';
import './styles/LayerSlideNumber.scss';
import type { LayerSwitchDirection } from '../context/LayerSwitchContext';
import { NumberCounter } from './NumberCounterComponent';
import { LAYER_SLIDE_MS } from '../constants/layerSlideAnimation';

type SlidePhase = 'idle' | 'exit-up' | 'exit-down' | 'enter-from-below' | 'enter-from-above';

export interface MultiplierDisplayInfo {
  value: number;
  symbol: string;
  isDivision: boolean;
  showSymbolAfter: boolean;
}

interface LayerSlideMultiplierProps {
  display: MultiplierDisplayInfo;
  direction: LayerSwitchDirection;
  switchNonce: number;
  slideEnabled?: boolean;
  animateFromZero?: boolean;
  duration?: number;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  style?: React.CSSProperties;
}

function slideClass(phase: SlidePhase): string {
  return phase === 'idle' ? '' : `layer-slide-${phase}`;
}

/** レイヤー切替時に ×N 表示を縦スライド（LayerSlideNumber / LayerSlideRoundUnit と同型） */
export const LayerSlideMultiplier: React.FC<LayerSlideMultiplierProps> = ({
  display,
  direction,
  switchNonce,
  slideEnabled = true,
  animateFromZero = false,
  duration = 300,
  className = '',
  onClick,
  style,
}) => {
  const [displaySlot, setDisplaySlot] = useState(display);
  const [animPhase, setAnimPhase] = useState<SlidePhase>('idle');
  const committedNonce = useRef(switchNonce);
  const pendingRef = useRef(display);
  pendingRef.current = display;
  const isAnimating = animPhase !== 'idle';
  const slidePending = direction !== 0 && switchNonce === committedNonce.current;
  const slideActive = switchNonce !== committedNonce.current || isAnimating;
  const countDuration = isAnimating ? 0 : duration;
  const phaseClass = slideActive ? slideClass(animPhase) : '';

  useEffect(() => {
    const pending = direction !== 0 && switchNonce === committedNonce.current;
    if (!slideEnabled && !pending && switchNonce === committedNonce.current) {
      committedNonce.current = switchNonce;
      return;
    }
    if (switchNonce === committedNonce.current) return;

    if (!slideEnabled) {
      setDisplaySlot(pendingRef.current);
      setAnimPhase('idle');
      committedNonce.current = switchNonce;
      return;
    }

    if (direction === 0) {
      setDisplaySlot(pendingRef.current);
      setAnimPhase('idle');
      committedNonce.current = switchNonce;
      return;
    }

    const exitPhase: SlidePhase = direction === 1 ? 'exit-up' : 'exit-down';
    const enterPhase: SlidePhase = direction === 1 ? 'enter-from-below' : 'enter-from-above';
    const targetNonce = switchNonce;

    setAnimPhase(exitPhase);

    const tEnter = window.setTimeout(() => {
      setDisplaySlot(pendingRef.current);
      setAnimPhase(enterPhase);
    }, LAYER_SLIDE_MS);

    const tIdle = window.setTimeout(() => {
      setAnimPhase('idle');
      committedNonce.current = targetNonce;
    }, LAYER_SLIDE_MS * 2);

    return () => {
      window.clearTimeout(tEnter);
      window.clearTimeout(tIdle);
    };
  }, [switchNonce, direction, slideEnabled]);

  useEffect(() => {
    if (slidePending || slideActive) return;
    setDisplaySlot(display);
  }, [display, switchNonce, slidePending, slideActive]);

  const wrapperClass = ['layer-slide-formatted-value', 'multiplier-slide-value', className, phaseClass]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={wrapperClass} onClick={onClick} style={style}>
      {displaySlot.isDivision ? '/' : '×'}{' '}
      <NumberCounter
        value={displaySlot.value}
        animateFromZero={animateFromZero && !isAnimating}
        duration={countDuration}
        suffix={displaySlot.showSymbolAfter ? displaySlot.symbol : ''}
        bridgePercentModeTransition
        suppressTooltip
      />
    </span>
  );
};
